import { apiClient } from '@/utils/api';

export type AttendanceEventType =
  | 'CHECK_IN'
  | 'CHECK_OUT'
  | 'BREAK_START'
  | 'BREAK_END'
  | 'PERMISSION_START'
  | 'PERMISSION_END';

export type AttendanceEventReason =
  | 'QR_UNAVAILABLE'
  | 'DEVICE_ISSUE'
  | 'FORGOT_TO_STAMP'
  | 'EMERGENCY'
  | 'SMART_WORKING_DAILY_DECLARATION'
  | 'FIELD_WORK'
  | 'OTHER';

export interface AttendanceActionPreset {
  id: string;
  code: string;
  label: string;
  eventType: AttendanceEventType;
  permissionKind?: string | null;
  icon?: string | null;
  order: number;
  isActive: boolean;
}

export interface ScanContext {
  locationName: string;
  presets: AttendanceActionPreset[];
  expiresAt: string;
}

export interface PresenceStatus {
  needsConfirm: boolean;
  canConfirmFromThisIp: boolean;
  pendingCheckInAt?: string | null;
}

const PRESET_CACHE_KEY = 'attendance.presets.v1';
const PRESET_CACHE_TTL_MS = 5 * 60 * 1000;

export async function getActionPresets(): Promise<AttendanceActionPreset[]> {
  const cached = localStorage.getItem(PRESET_CACHE_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as { at: number; data: AttendanceActionPreset[] };
      if (Date.now() - parsed.at < PRESET_CACHE_TTL_MS) {
        return parsed.data;
      }
    } catch {
      localStorage.removeItem(PRESET_CACHE_KEY);
    }
  }

  const response = await apiClient.get('/attendance/action-presets');
  const data = response.data.data as AttendanceActionPreset[];
  localStorage.setItem(PRESET_CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  return data;
}

export async function getScanContext(chiave: string): Promise<ScanContext> {
  const response = await apiClient.get('/attendance/scan-context', { params: { chiave } });
  return response.data.data as ScanContext;
}

export async function postAttendanceEvent(payload: {
  chiave?: string;
  presetId: string;
  source: 'QR_SCAN' | 'PC_MANUAL';
  reason?: AttendanceEventReason;
  note?: string;
}) {
  const response = await apiClient.post('/attendance/events', payload);
  return response.data.data as { event: any; requiresPresenceConfirmation: boolean };
}

export async function getPresenceStatus(): Promise<PresenceStatus> {
  const response = await apiClient.get('/attendance/me/presence-status');
  return response.data.data as PresenceStatus;
}

export async function confirmPresence() {
  const response = await apiClient.post('/attendance/confirm-presence');
  return response.data.data;
}

export async function getMyAttendanceEvents(from: string, to: string) {
  const response = await apiClient.get('/attendance/me/events', { params: { from, to } });
  return response.data.data;
}

export async function getMyAttendanceDay(date: string) {
  const response = await apiClient.get(`/attendance/me/day/${date}`);
  return response.data.data;
}

export async function getMyAttendanceAnomalies() {
  const response = await apiClient.get('/attendance/me/anomalies');
  return response.data.data;
}

export async function getMyAttendanceMonth(year: number, month: number) {
  const response = await apiClient.get('/attendance/me/month', { params: { year, month } });
  return response.data.data;
}

export type AbsenceType =
  | 'FERIE'
  | 'MALATTIA'
  | 'ROL'
  | 'EX_FESTIVITA'
  | 'LEGGE_104'
  | 'CONGEDO_MATRIMONIO'
  | 'CONGEDO_PARENTALE'
  | 'INFORTUNIO'
  | 'LUTTO'
  | 'ALTRO';

export const ABSENCE_TYPE_LABELS: Record<AbsenceType, string> = {
  FERIE: 'Ferie',
  MALATTIA: 'Malattia',
  ROL: 'ROL',
  EX_FESTIVITA: 'Ex festività',
  LEGGE_104: 'Legge 104',
  CONGEDO_MATRIMONIO: 'Congedo matrimoniale',
  CONGEDO_PARENTALE: 'Congedo parentale',
  INFORTUNIO: 'Infortunio',
  LUTTO: 'Lutto',
  ALTRO: 'Altro',
};

export type AbsenceRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface AbsenceRecord {
  id: string;
  userId: string;
  absenceType: AbsenceType;
  customCode?: string | null;
  startDate: string;
  endDate: string;
  isFullDay: boolean;
  permissionStart?: string | null;
  permissionEnd?: string | null;
  note?: string | null;
  status: AbsenceRequestStatus;
  rejectionNote?: string | null;
  reviewedAt?: string | null;
  medicalCertificateNumber?: string | null;
  medicalCertificateSubmittedAt?: string | null;
  user?: { id: string; firstName: string; lastName: string; employeeCode?: string | null };
  requestedBy?: { id: string; firstName: string; lastName: string } | null;
  reviewedBy?: { id: string; firstName: string; lastName: string } | null;
}

export interface AbsenceRequestPayload {
  absenceType: AbsenceType;
  customCode?: string;
  startDate: string;
  endDate: string;
  isFullDay: boolean;
  permissionStart?: string;
  permissionEnd?: string;
  note?: string;
  medicalCertificateNumber?: string;
}

export async function listMyAbsenceRequests(): Promise<AbsenceRecord[]> {
  const response = await apiClient.get('/attendance/me/absence-requests');
  return response.data.data as AbsenceRecord[];
}

export async function createMyAbsenceRequest(payload: AbsenceRequestPayload): Promise<AbsenceRecord> {
  const response = await apiClient.post('/attendance/me/absence-requests', payload);
  return response.data.data as AbsenceRecord;
}

export async function deleteMyAbsenceRequest(id: string): Promise<void> {
  await apiClient.delete(`/attendance/me/absence-requests/${id}`);
}

export async function submitMalattiaCertificate(payload: {
  medicalCertificateNumber: string;
  startDate?: string;
  endDate: string;
}): Promise<AbsenceRecord> {
  const response = await apiClient.post('/attendance/me/malattia-certificates', payload);
  return response.data.data as AbsenceRecord;
}

export async function submitAbsenceCertificate(id: string, medicalCertificateNumber: string): Promise<AbsenceRecord> {
  const response = await apiClient.patch(`/attendance/me/absences/${id}/certificate`, { medicalCertificateNumber });
  return response.data.data as AbsenceRecord;
}

export type CalendarAbsenceSummary = {
  id: string;
  absenceType: AbsenceType;
  status: AbsenceRequestStatus;
  isFullDay: boolean;
  hours: number;
  medicalCertificateMissing: boolean;
};

export async function listAdminAbsenceRequests(status?: AbsenceRequestStatus): Promise<AbsenceRecord[]> {
  const response = await apiClient.get('/attendance/admin/absence-requests', {
    params: status ? { status } : undefined,
  });
  return response.data.data as AbsenceRecord[];
}

export async function approveAbsenceRequest(id: string): Promise<AbsenceRecord> {
  const response = await apiClient.post(`/attendance/admin/absence-requests/${id}/approve`);
  return response.data.data as AbsenceRecord;
}

export async function rejectAbsenceRequest(id: string, rejectionNote: string): Promise<AbsenceRecord> {
  const response = await apiClient.post(`/attendance/admin/absence-requests/${id}/reject`, { rejectionNote });
  return response.data.data as AbsenceRecord;
}

export interface AttendanceBalances {
  vacationHours: number;
  rolHours: number;
  exFestivitaHours: number;
  hourBankBalance: number;
  showHourBank: boolean;
  hoursPerDay: number;
  weeklyTargetHours: number;
  selectedWeekExpectedHours: number;
  currentWeekExpectedHours: number;
  monthMinutes: number;
  selectedWeekMinutes: number;
  currentWeekMinutes: number;
  selectedWeekDeltaHours: number;
  currentWeekDeltaHours: number;
}

export interface AdminBalanceRow extends AttendanceBalances {
  user: { id: string; firstName: string; lastName: string; employeeCode?: string | null };
}

export async function getMyBalances(year: number, month: number): Promise<AttendanceBalances> {
  const response = await apiClient.get('/attendance/me/balances', { params: { year, month } });
  return response.data.data as AttendanceBalances;
}

export async function getAdminBalances(userIds: string[], year: number, month: number): Promise<AdminBalanceRow[]> {
  const response = await apiClient.get('/attendance/admin/balances', {
    params: { year, month, ...(userIds.length > 0 ? { userIds: userIds.join(',') } : {}) },
  });
  return response.data.data as AdminBalanceRow[];
}

export async function setUserHourBank(userId: string, value: number, note: string): Promise<number> {
  const response = await apiClient.post(`/attendance/admin/users/${userId}/hour-bank`, { value, note });
  return response.data.data.hourBankBalance as number;
}

async function downloadBlob(response: { data: BlobPart; headers?: unknown }, fallbackName: string) {
  const blob = new Blob([response.data as BlobPart]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const headers = response.headers as Record<string, string> | undefined;
  const disposition = headers?.['content-disposition'];
  const match = disposition?.match(/filename="?([^"]+)"?/);
  link.download = match?.[1] ?? fallbackName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function getMonthSealStatus(year: number, month: number): Promise<{ sealed: boolean }> {
  const response = await apiClient.get('/attendance/admin/month/seal', { params: { year, month } });
  return response.data.data as { sealed: boolean };
}

export async function downloadAttendanceExport(
  year: number,
  month: number,
  format: 'csv' | 'xlsx',
  userIds?: string[],
): Promise<void> {
  const response = await apiClient.get('/attendance/admin/export', {
    params: { year, month, format, ...(userIds && userIds.length > 0 ? { userIds: userIds.join(',') } : {}) },
    responseType: 'blob',
  });
  const subset = userIds && userIds.length > 0 ? `_subset-${userIds.length}` : '';
  const name = format === 'xlsx'
    ? `presenze-${year}-${String(month).padStart(2, '0')}${subset}.xlsx`
    : `payroll-${year}-${String(month).padStart(2, '0')}${subset}.csv`;
  await downloadBlob(response, name);
}

export async function downloadTeamSystemExport(
  year: number,
  month: number,
  tracciato: 'RILPRE' | 'OREPAGG',
  userIds?: string[],
): Promise<void> {
  const response = await apiClient.get('/attendance/admin/export/teamsystem', {
    params: { year, month, tracciato, ...(userIds && userIds.length > 0 ? { userIds: userIds.join(',') } : {}) },
    responseType: 'blob',
  });
  await downloadBlob(response, `teamsystem-${tracciato.toLowerCase()}-${year}-${String(month).padStart(2, '0')}.txt`);
}

export interface CompanyHolidayRange {
  id: string;
  startDate: string;
  endDate: string;
  label: string;
  isClosure: boolean;
  createdAt: string;
}

export async function listCompanyHolidayRanges(): Promise<CompanyHolidayRange[]> {
  const response = await apiClient.get('/attendance/admin/company-holiday-ranges');
  return response.data.data as CompanyHolidayRange[];
}

export async function createCompanyHolidayRange(payload: {
  startDate: string;
  endDate: string;
  label: string;
  isClosure?: boolean;
}): Promise<CompanyHolidayRange> {
  const response = await apiClient.post('/attendance/admin/company-holiday-ranges', payload);
  return response.data.data as CompanyHolidayRange;
}

export async function updateCompanyHolidayRange(
  id: string,
  payload: { startDate: string; endDate: string; label: string; isClosure?: boolean },
): Promise<CompanyHolidayRange> {
  const response = await apiClient.patch(`/attendance/admin/company-holiday-ranges/${id}`, payload);
  return response.data.data as CompanyHolidayRange;
}

export async function deleteCompanyHolidayRange(id: string): Promise<void> {
  await apiClient.delete(`/attendance/admin/company-holiday-ranges/${id}`);
}
